'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Routes } from '@/settings/routes';
import { FaUserGroup, FaMinus, FaPlus, FaCalendarDays } from 'react-icons/fa6';
import { RiSearchLine } from 'react-icons/ri';
import { HiArrowRight, HiChevronLeft, HiChevronRight } from 'react-icons/hi2';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function getDaysInMonth(year, month) {
    return new Date(year, month + 1, 0).getDate();
}
function getFirstDayOfMonth(year, month) {
    return new Date(year, month, 1).getDay();
}
function isSameDay(a, b) {
    return a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function formatDisplay(date) {
    if (!date) return '';
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ─── Single Date Popover ──────────────────────────────────────────────────────
function SingleDatePopover({ selectedDate, minDate, onChange, onClose }) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const min = minDate ? new Date(new Date(minDate).setHours(0, 0, 0, 0)) : today;

    const [viewYear, setViewYear] = useState(selectedDate ? selectedDate.getFullYear() : min.getFullYear());
    const [viewMonth, setViewMonth] = useState(selectedDate ? selectedDate.getMonth() : min.getMonth());

    const prevMonth = () => {
        if (viewMonth === 0) {
            setViewMonth(11);
            setViewYear((y) => y - 1);
        } else setViewMonth((m) => m - 1);
    };
    const nextMonth = () => {
        if (viewMonth === 11) {
            setViewMonth(0);
            setViewYear((y) => y + 1);
        } else setViewMonth((m) => m + 1);
    };

    const daysInMonth = getDaysInMonth(viewYear, viewMonth);
    const firstDay = getFirstDayOfMonth(viewYear, viewMonth);
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(viewYear, viewMonth, d));

    return (
        <>
            <div className="fixed inset-0 z-30" onClick={onClose} />
            <div className="absolute left-0 bottom-full z-40 p-5 mb-3 bg-white rounded-2xl border border-gray-100" style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.15)', minWidth: '280px' }}>
                {/* Navigation */}
                <div className="flex justify-between items-center mb-4">
                    <button type="button" onClick={prevMonth} className="flex justify-center items-center w-8 h-8 text-gray-500 rounded-lg transition-colors hover:bg-gray-100 hover:text-gray-800">
                        <HiChevronLeft className="size-5" />
                    </button>
                    <span className="text-sm font-bold text-gray-800">
                        {MONTHS[viewMonth]} {viewYear}
                    </span>
                    <button type="button" onClick={nextMonth} className="flex justify-center items-center w-8 h-8 text-gray-500 rounded-lg transition-colors hover:bg-gray-100 hover:text-gray-800">
                        <HiChevronRight className="size-5" />
                    </button>
                </div>

                {/* Day headers */}
                <div className="grid grid-cols-7 mb-1">
                    {DAYS.map((d) => (
                        <div key={d} className="py-1 text-[0.65rem] font-bold text-center text-gray-400 uppercase">
                            {d}
                        </div>
                    ))}
                </div>

                {/* Day cells */}
                <div className="grid grid-cols-7">
                    {cells.map((date, i) => {
                        if (!date) return <div key={`e-${i}`} />;
                        const isPast = date < min;
                        const isSelected = isSameDay(date, selectedDate);
                        const isToday = isSameDay(date, today);

                        return (
                            <button
                                key={date.toISOString()}
                                type="button"
                                disabled={isPast}
                                onClick={() => {
                                    onChange(date);
                                    onClose();
                                }}
                                className={`
                                    relative h-9 text-xs font-medium rounded-lg transition-all duration-150 select-none
                                    ${isPast ? 'text-gray-300 cursor-not-allowed' : 'cursor-pointer'}
                                    ${isSelected ? 'font-bold text-white' : ''}
                                    ${!isSelected && !isPast ? 'text-gray-700 hover:bg-orange-50 hover:text-main-color' : ''}
                                `}
                                style={{ backgroundColor: isSelected ? '#E8604C' : undefined }}
                            >
                                {isToday && !isSelected && <span className="absolute bottom-1 left-1/2 w-1 h-1 rounded-full -translate-x-1/2 bg-main-color" />}
                                {date.getDate()}
                            </button>
                        );
                    })}
                </div>
            </div>
        </>
    );
}

// ─── Main Form ────────────────────────────────────────────────────────────────
export default function HeroBookingForm() {
    const router = useRouter();
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [showStartCal, setShowStartCal] = useState(false);
    const [showEndCal, setShowEndCal] = useState(false);
    const [showGuests, setShowGuests] = useState(false);
    const [guests, setGuests] = useState({ adult: 1, youth: 0, children: 0 });

    const totalGuests = guests.adult + guests.youth + guests.children;

    const updateGuest = (type, delta) => {
        setGuests((prev) => ({
            ...prev,
            [type]: Math.max(type === 'adult' ? 1 : 0, prev[type] + delta),
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const params = new URLSearchParams();
        if (startDate) params.set('start', startDate.toISOString().split('T')[0]);
        if (endDate) params.set('end', endDate.toISOString().split('T')[0]);
        params.set('adult', guests.adult);
        if (guests.youth > 0) params.set('youth', guests.youth);
        if (guests.children > 0) params.set('children', guests.children);
        router.push(`${Routes.FRONT_tailor_made}?${params.toString()}`);
    };

    return (
        <form onSubmit={handleSubmit} className="w-full">
            <div className="flex flex-col gap-3 p-4 rounded-2xl shadow-2xl backdrop-blur-sm bg-white/95 lg:flex-row lg:items-center lg:gap-0 lg:p-2">
                {/* ── Start Date ── */}
                <div className="relative flex-1">
                    <button
                        type="button"
                        onClick={() => {
                            setShowStartCal(!showStartCal);
                            setShowEndCal(false);
                            setShowGuests(false);
                        }}
                        className="flex flex-col gap-1 px-4 py-3 w-full text-left rounded-xl transition-colors hover:bg-gray-50 lg:border-r lg:border-gray-200 lg:rounded-none"
                    >
                        <span className="text-xs font-semibold tracking-wide text-gray-500 uppercase">Start Date</span>
                        <div className="flex gap-2 items-center">
                            <FaCalendarDays className="text-main-color size-4 shrink-0" />
                            <span className={`text-sm font-medium ${startDate ? 'text-gray-800' : 'text-gray-400'}`}>{startDate ? formatDisplay(startDate) : 'Add date'}</span>
                        </div>
                    </button>
                    {showStartCal && (
                        <SingleDatePopover
                            selectedDate={startDate}
                            minDate={null}
                            onChange={(date) => {
                                setStartDate(date);
                                if (endDate && date > endDate) setEndDate(null);
                            }}
                            onClose={() => setShowStartCal(false)}
                        />
                    )}
                </div>

                {/* ── End Date ── */}
                <div className="relative flex-1">
                    <button
                        type="button"
                        onClick={() => {
                            setShowEndCal(!showEndCal);
                            setShowStartCal(false);
                            setShowGuests(false);
                        }}
                        className="flex flex-col gap-1 px-4 py-3 w-full text-left rounded-xl transition-colors hover:bg-gray-50 lg:border-r lg:border-gray-200 lg:rounded-none"
                    >
                        <span className="text-xs font-semibold tracking-wide text-gray-500 uppercase">End Date</span>
                        <div className="flex gap-2 items-center">
                            <FaCalendarDays className="text-main-color size-4 shrink-0" />
                            <span className={`text-sm font-medium ${endDate ? 'text-gray-800' : 'text-gray-400'}`}>{endDate ? formatDisplay(endDate) : 'Add date'}</span>
                        </div>
                    </button>
                    {showEndCal && (
                        <SingleDatePopover selectedDate={endDate} minDate={startDate ? new Date(startDate) : new Date()} onChange={(date) => setEndDate(date)} onClose={() => setShowEndCal(false)} />
                    )}
                </div>

                {/* ── Guests ── */}
                <div className="relative flex-1">
                    <button
                        type="button"
                        onClick={() => {
                            setShowGuests(!showGuests);
                            setShowStartCal(false);
                            setShowEndCal(false);
                        }}
                        className="flex flex-col gap-1 px-4 py-3 w-full rounded-xl transition-colors hover:bg-gray-50"
                    >
                        <span className="text-xs font-semibold tracking-wide text-left text-gray-500 uppercase">Guests</span>
                        <div className="flex gap-2 items-center">
                            <FaUserGroup className="text-main-color size-4" />
                            <span className="text-sm font-medium text-gray-800">
                                {totalGuests} {totalGuests === 1 ? 'Guest' : 'Guests'}
                            </span>
                        </div>
                    </button>

                    {showGuests && (
                        <>
                            <div className="fixed inset-0 z-30" onClick={() => setShowGuests(false)} />
                            <div
                                className="absolute right-0 left-0 bottom-full z-40 p-5 mb-2 bg-white rounded-xl border border-gray-100 shadow-xl lg:left-auto lg:min-w-[280px]"
                                style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}
                            >
                                {[
                                    { key: 'adult', label: 'Adults', desc: '18+ years' },
                                    { key: 'youth', label: 'Youth', desc: '12-17 years' },
                                    { key: 'children', label: 'Children', desc: '2-11 years' },
                                ].map(({ key, label, desc }) => (
                                    <div key={key} className="flex justify-between items-center py-3 border-b border-gray-100 last:border-0">
                                        <div>
                                            <p className="text-sm font-medium text-gray-800">{label}</p>
                                            <p className="text-xs text-gray-500">{desc}</p>
                                        </div>
                                        <div className="flex gap-3 items-center">
                                            <button
                                                type="button"
                                                onClick={() => updateGuest(key, -1)}
                                                disabled={key === 'adult' ? guests[key] <= 1 : guests[key] <= 0}
                                                className="flex justify-center items-center rounded-full border border-gray-300 transition-colors size-8 hover:border-main-color hover:text-main-color disabled:opacity-30 disabled:cursor-not-allowed"
                                            >
                                                <FaMinus className="size-3" />
                                            </button>
                                            <span className="w-6 text-sm font-semibold text-center text-gray-800">{guests[key]}</span>
                                            <button
                                                type="button"
                                                onClick={() => updateGuest(key, 1)}
                                                className="flex justify-center items-center rounded-full border border-gray-300 transition-colors size-8 hover:border-main-color hover:text-main-color"
                                            >
                                                <FaPlus className="size-3" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                                <button type="button" onClick={() => setShowGuests(false)} className="py-2 mt-3 w-full text-sm font-semibold text-white rounded-lg transition-colors btn hover-bg">
                                    Apply
                                </button>
                            </div>
                        </>
                    )}
                </div>

                {/* ── Submit ── */}
                <button
                    type="submit"
                    className="flex gap-2 justify-center items-center px-8 py-4 text-sm font-bold text-white rounded-full transition-all duration-300 group hover:gap-4 hover:shadow-lg active:scale-95 shrink-0"
                    style={{ backgroundColor: '#E8604C' }}
                >
                    <RiSearchLine className="size-5" />
                    <span>Plan My Trip</span>
                    <HiArrowRight className="-ml-2 opacity-0 transition-all duration-300 size-4 group-hover:opacity-100 group-hover:ml-0" />
                </button>
            </div>
        </form>
    );
}
